import { isNil } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import {
  cameraConstants,
  colorConstants,
  graphRenderAttribute,
} from '../graph-viewer/constants';
import {
  calculateSpriteScale,
  createMarkerSprite,
  createShapeTexture,
  createSlopeLineGeometry,
  createSlopeMarkerSprite,
} from '../graph-viewer/helpers';
import { PointBatchRenderer } from '../graph-viewer/point-batch-renderer';
import type { ThreeJsRefs, UseCrossSection3dOptions } from './types';

const { backgroundColor } = graphRenderAttribute;

/**
 * Initializes the Three.js scene and renderer for the cross-section profile.
 * @param data - The cross-section data to render.
 * @param canvasContainerRef - The ref to the canvas container.
 * @param onInitComplete - The callback to call when the scene is initialized.
 * @param pointCloudLayerProperties - The point cloud layer properties.
 */
export const useCrossSection3d = ({
  data,
  canvasContainerRef,
  onInitComplete,
  pointCloudLayerProperties,
}: UseCrossSection3dOptions) => {
  const threeJsRefs = useRef<ThreeJsRefs>({
    scene: null,
    camera: null,
    renderer: null,
    batchRenderer: null,
    highlightSprite: null,
    slopePointASprite: null,
    slopePointBSprite: null,
    slopeLine: null,
  });

  const renderScene = useCallback(() => {
    const { renderer, scene, camera } = threeJsRefs.current;
    if (
      isNil(renderer) ||
      isNil(scene) ||
      isNil(camera) ||
      isNil(canvasContainerRef.current)
    ) {
      return;
    }

    const width = canvasContainerRef.current.clientWidth;
    const height = canvasContainerRef.current.clientHeight;

    renderer.setSize(width, height);
    renderer.setClearColor(backgroundColor, 0);
    renderer.clear(true, true, false);
    renderer.render(scene, camera);
  }, [canvasContainerRef]);

  const updateHighlightSprite = useCallback(
    (mileage: number, elevation: number) => {
      const { highlightSprite, camera } = threeJsRefs.current;
      if (
        isNil(highlightSprite) ||
        isNil(camera) ||
        isNil(canvasContainerRef.current)
      ) {
        return;
      }

      highlightSprite.visible = true;
      highlightSprite.position.set(mileage, 0, elevation);

      const worldSize = calculateSpriteScale(
        camera,
        canvasContainerRef.current.clientHeight,
      );
      highlightSprite.scale.set(worldSize, worldSize, 1);
    },
    [canvasContainerRef],
  );

  const hideHighlightSprite = useCallback(() => {
    const { highlightSprite } = threeJsRefs.current;
    if (!isNil(highlightSprite)) {
      highlightSprite.visible = false;
    }
  }, []);

  const isInitializedRef = useRef(false);
  const materialRef = useRef<THREE.PointsMaterial | null>(null);

  const initializeThreeJs = useCallback(async () => {
    const {
      bounds: cameraBounds,
      near: cameraNear,
      far: cameraFar,
    } = cameraConstants;
    const { batchSize } = graphRenderAttribute;

    if (isInitializedRef.current && !isNil(threeJsRefs.current.batchRenderer)) {
      threeJsRefs.current.batchRenderer.dispose();
      threeJsRefs.current.batchRenderer = null;
      if (threeJsRefs.current.renderer?.domElement.parentElement) {
        threeJsRefs.current.renderer.domElement.remove();
      }
      threeJsRefs.current.renderer?.dispose();
      threeJsRefs.current.renderer = null;
      if (materialRef.current) {
        materialRef.current.map?.dispose();
        materialRef.current.dispose();
        materialRef.current = null;
      }
      isInitializedRef.current = false;
    }

    if (isNil(canvasContainerRef.current) || isNil(data) || data.count === 0) {
      return;
    }

    const width = canvasContainerRef.current.clientWidth;
    const height = canvasContainerRef.current.clientHeight;

    // Initialize renderer
    // preserveDrawingBuffer is essential for capturing WebGL canvas with html2canvas
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      premultipliedAlpha: false,
      preserveDrawingBuffer: true,
    });
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    renderer.setClearColor(backgroundColor, 0);
    renderer.setSize(width, height);
    renderer.autoClear = false;
    canvasContainerRef.current.appendChild(renderer.domElement);

    // Initialize camera
    const camera = new THREE.OrthographicCamera(
      -cameraBounds,
      cameraBounds,
      cameraBounds,
      -cameraBounds,
      cameraNear,
      cameraFar,
    );
    camera.up.set(0, 0, 1);
    camera.rotation.order = 'ZXY';
    camera.rotation.x = Math.PI / 2;

    // Initialize scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(backgroundColor);

    // Create highlight sprite
    const highlightSprite = createMarkerSprite(colorConstants.highlight);
    scene.add(highlightSprite);

    // Get point cloud properties
    const { pointSize, opacity, pointShape } = pointCloudLayerProperties;

    const lineMaterial = new THREE.MeshBasicMaterial({
      color: 0xb01212,
      transparent: true,
      opacity: 0.8,
    });

    const initialGeometry = createSlopeLineGeometry(
      { mileage: 0, elevation: 0 },
      { mileage: 0, elevation: 0 },
      0.1,
    );

    const slopeLine = new THREE.Mesh(initialGeometry, lineMaterial);
    slopeLine.visible = false;
    slopeLine.renderOrder = -1;
    scene.add(slopeLine);

    const slopePointA = createSlopeMarkerSprite('A');
    scene.add(slopePointA);

    const slopePointB = createSlopeMarkerSprite('B');
    scene.add(slopePointB);

    // Create point material
    const pointTexture = createShapeTexture(pointShape);
    const normalizedOpacity = opacity ? opacity / 100 : 1;
    const material = new THREE.PointsMaterial({
      size: pointSize,
      vertexColors: true,
      sizeAttenuation: false,
      opacity: normalizedOpacity,
      transparent: normalizedOpacity < 1.0,
      depthTest: false,
      alphaTest: 0.01,
      depthWrite: false,
      map: pointTexture,
    });
    materialRef.current = material;

    // Initialize batch renderer
    const batchRenderer = new PointBatchRenderer(scene, {
      batchSize: batchSize,
      material,
    });

    // Store refs
    threeJsRefs.current = {
      scene,
      camera,
      renderer,
      batchRenderer,
      highlightSprite,
      slopePointASprite: slopePointA,
      slopePointBSprite: slopePointB,
      slopeLine,
    };

    batchRenderer.loadData(data);
    const bounds = batchRenderer.getBounds();
    isInitializedRef.current = true;
    onInitComplete(bounds);
    renderScene();

    return () => {
      isInitializedRef.current = false;
      batchRenderer.dispose();
      material.map?.dispose();
      material.dispose();
      materialRef.current = null;
      if (renderer.domElement.parentElement) {
        renderer.domElement.remove();
      }
      renderer.dispose();
    };
  }, [
    data,
    pointCloudLayerProperties,
    canvasContainerRef,
    onInitComplete,
    renderScene,
  ]);

  useEffect(() => {
    initializeThreeJs();
    return () => {
      const refs = threeJsRefs.current;
      if (!isNil(refs.batchRenderer)) {
        refs.batchRenderer.dispose();
        threeJsRefs.current.batchRenderer = null;
      }
      if (!isNil(refs.renderer)) {
        if (refs.renderer.domElement.parentElement) {
          refs.renderer.domElement.remove();
        }
        refs.renderer.dispose();
        threeJsRefs.current.renderer = null;
      }
      if (!isNil(materialRef.current)) {
        materialRef.current.map?.dispose();
        materialRef.current.dispose();
        materialRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, [initializeThreeJs]);

  // Cleanup sprites
  useEffect(() => {
    return () => {
      const {
        highlightSprite,
        slopePointASprite,
        slopePointBSprite,
        slopeLine,
      } = threeJsRefs.current;
      if (!isNil(highlightSprite)) {
        highlightSprite.material.map?.dispose();
        highlightSprite.material.dispose();
      }
      if (!isNil(slopePointASprite)) {
        slopePointASprite.material.map?.dispose();
        slopePointASprite.material.dispose();
      }
      if (!isNil(slopePointBSprite)) {
        slopePointBSprite.material.map?.dispose();
        slopePointBSprite.material.dispose();
      }
      if (!isNil(slopeLine)) {
        slopeLine.geometry.dispose();
      }
    };
  }, []);

  return {
    threeJsRefs,
    renderScene,
    updateHighlightSprite,
    hideHighlightSprite,
  };
};
